import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  Tooltip,
  Autocomplete,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { CheckCircle, Edit } from '@mui/icons-material';

const VALID_UNITS = [
  'oz', 'ml', 'cl', 'dash', 'drop', 'tsp', 'tbsp', 'barspoon', 'pinch', 'cup'
];

const ComparisonView = ({ parsedLines, inventory = [], onAccept, onEdit }) => {
  const [editedLines, setEditedLines] = useState(parsedLines || []);
  const safeInventory = Array.isArray(inventory) ? inventory : [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getLinesOfType = (type) => editedLines.filter(line => line.type === type);

  const nameLines = getLinesOfType('name');
  const descriptionLines = getLinesOfType('description');
  const ingredientLines = getLinesOfType('ingredient');
  const instructionLines = getLinesOfType('instruction');
  const garnishLines = getLinesOfType('garnish');
  const glassLines = getLinesOfType('glass');

  const handleLineChange = (index, field, value) => {
    const newLines = [...editedLines];
    if (field === 'amount' || field === 'name' || field === 'unit') {
      newLines[index].parsed[field] = value;
    } else {
      newLines[index].parsed.value = value;
    }
    setEditedLines(newLines);
  };

  const handleAccept = () => {
    onAccept(editedLines);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI Translation Results
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={isMobile ? 12 : 5}>
            <Typography variant="subtitle1" gutterBottom>Original Text</Typography>
          </Grid>
          <Grid item xs={isMobile ? 12 : 7}>
            <Typography variant="subtitle1" gutterBottom>Interpreted As (Click to Edit)</Typography>
          </Grid>

          {/* Name Section */}
          {nameLines.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Recipe Name
                </Typography>
              </Grid>
              {nameLines.map((line, index) => (
                <React.Fragment key={`name-${index}`}>
                  <Grid item xs={isMobile ? 12 : 5}>
                    <Typography>{line.original}</Typography>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 7}>
                    <TextField
                      fullWidth
                      size="small"
                      value={line.parsed.value || ''}
                      onChange={(e) => handleLineChange(editedLines.indexOf(line), 'value', e.target.value)}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}

          {/* Ingredients Section */}
          {ingredientLines.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Ingredients
                </Typography>
              </Grid>
              {ingredientLines.map((line, index) => (
                <React.Fragment key={`ing-${index}`}>
                  <Grid item xs={isMobile ? 12 : 5}>
                    <Typography>{line.original}</Typography>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 7}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1 
                    }}>
                      {/* Ingredient row on mobile */}
                      {isMobile && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Ingredient"
                          value={line.parsed.name || ''}
                          onChange={(e) => handleLineChange(editedLines.indexOf(line), 'name', e.target.value)}
                          InputProps={{
                            endAdornment: safeInventory.some(i => 
                              i.name.toLowerCase() === (line.parsed.name || '').toLowerCase()
                            ) && (
                              <Tooltip title="Ingredient found in database">
                                <CheckCircle color="success" fontSize="small" />
                              </Tooltip>
                            )
                          }}
                        />
                      )}
                      
                      {/* Amount and Unit row - side by side on all screens */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        width: '100%'
                      }}>
                        <TextField
                          size="small"
                          value={line.parsed.amount || ''}
                          onChange={(e) => handleLineChange(editedLines.indexOf(line), 'amount', e.target.value)}
                          placeholder="Amount"
                          label={isMobile ? "Amount" : undefined}
                          sx={{ 
                            width: isMobile ? '50%' : '80px',
                            flexShrink: 0
                          }}
                        />
                        <TextField
                          select
                          size="small"
                          value={line.parsed.unit || 'oz'}
                          onChange={(e) => handleLineChange(editedLines.indexOf(line), 'unit', e.target.value)}
                          label={isMobile ? "Unit" : undefined}
                          sx={{ 
                            width: isMobile ? '50%' : '80px',
                            flexShrink: 0
                          }}
                        >
                          {VALID_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      
                      {/* Desktop-only ingredient field */}
                      {!isMobile && (
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={safeInventory.map(i => i.name)}
                          value={line.parsed.name || ''}
                          onChange={(_, newValue) => handleLineChange(editedLines.indexOf(line), 'name', newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{ minWidth: '200px' }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {safeInventory.some(i => 
                                      i.name.toLowerCase() === (line.parsed.name || '').toLowerCase()
                                    ) && (
                                      <Tooltip title="Ingredient found in database">
                                        <CheckCircle color="success" fontSize="small" />
                                      </Tooltip>
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      )}
                    </Box>
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}

          {/* Instructions Section */}
          {instructionLines.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Instructions
                </Typography>
              </Grid>
              {instructionLines.map((line, index) => (
                <React.Fragment key={`inst-${index}`}>
                  <Grid item xs={isMobile ? 12 : 5}>
                    <Typography>{line.original}</Typography>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 7}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      value={line.parsed.value || ''}
                      onChange={(e) => handleLineChange(editedLines.indexOf(line), 'value', e.target.value)}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}

          {/* Garnish Section */}
          {garnishLines.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Garnish
                </Typography>
              </Grid>
              {garnishLines.map((line, index) => (
                <React.Fragment key={`garn-${index}`}>
                  <Grid item xs={isMobile ? 12 : 5}>
                    <Typography>{line.original}</Typography>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 7}>
                    <TextField
                      fullWidth
                      size="small"
                      value={line.parsed.value || ''}
                      onChange={(e) => handleLineChange(editedLines.indexOf(line), 'value', e.target.value)}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}

          {/* Glass Section */}
          {glassLines.length > 0 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                  Glass
                </Typography>
              </Grid>
              {glassLines.map((line, index) => (
                <React.Fragment key={`glass-${index}`}>
                  <Grid item xs={isMobile ? 12 : 5}>
                    <Typography>{line.original}</Typography>
                  </Grid>
                  <Grid item xs={isMobile ? 12 : 7}>
                    <TextField
                      fullWidth
                      size="small"
                      value={line.parsed.value || ''}
                      onChange={(e) => handleLineChange(editedLines.indexOf(line), 'value', e.target.value)}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={() => onEdit(editedLines)}
            startIcon={<Edit />}
          >
            Edit in Full Form
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAccept}
            startIcon={<CheckCircle />}
          >
            Accept & Continue
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ComparisonView; 